import { useEffect, useMemo } from 'react';
import usePrevious from '../../../hooks/usePrevious';
import { useAuthState } from '../context/authContext';

interface useAuthSessionOptions {
    /** If true, then it will redirect to login if not authed */
    required?: boolean;
    /** required must be true for this to run, if a user is not authed and we require them to be authed, redirectToLogin is run */
    redirectToLogin?: () => void;
    /** This runs when changing from authed to non authed */
    onUnauthenticated?: () => void;
}

const useAuthSession = (options?: useAuthSessionOptions) => {
    const { required, redirectToLogin, onUnauthenticated } = options || {};
    const { session, status } = useAuthState();

    const prevSession = usePrevious(session);

    // A single effect, not two: when the user was authed and now isn't AND
    // auth is required, both onUnauthenticated and redirectToLogin used to
    // fire on the same transition - in practice callers wire both to the
    // same signIn() call, which raced two concurrent OAuth flows against
    // each other (each writing its own state/pkce cookie pair and its own
    // /oauth/authorize request) and produced an intermittent NextAuth
    // "state mismatch" error on the very next login after a logout.
    // onUnauthenticated takes priority since it's the more specific case;
    // redirectToLogin only runs when it doesn't apply.
    useEffect(() => {
        if (session) {
            return;
        }

        if (prevSession && onUnauthenticated) {
            onUnauthenticated();
        } else if (required && redirectToLogin) {
            redirectToLogin();
        }
    }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

    const memoValue = useMemo(() => ({ session, status }), [session, status]);
    return memoValue;
};

export default useAuthSession;
